import { useGetIncidentSuspense } from 'core/gen/endpoints/incidents/incidents';
import {
  AcceptRequestHistorySchema,
  IncidentDetailGetResponse,
} from 'core/gen/schemas';
import { formatDateTime } from 'core/utils/date';
import { useParams } from 'react-router-dom';

export const useIncidentDetail = () => {
  const { incidentId } = useParams();
  if (!incidentId) {
    throw new Error('incidentId is required');
  }

  const { data, isLoading, isError } = useGetIncidentSuspense(incidentId);

  return {
    incidentDetailData: getResponseToIncidentDetail(data),
    isLoading,
    isError,
  };
};

const getResponseToIncidentDetail = (
  incidentDetail: IncidentDetailGetResponse
) => {
  const { incidentInfo, emsTeamInfo, patientInfo } = incidentDetail;
  let unknownCounter = 1;
  return {
    incidentInfo: {
      ...incidentInfo,
      incidentNo: incidentInfo.incidentNo ?? '',
      telegramTargetAddress: incidentInfo.telegramTargetAddress ?? '',
    },
    patientInfo: patientInfo.map(patient => {
      return {
        ...patient,
        note: patient.note ?? '',
        determinedHospitalName: getDeterminedHospitalName(
          patient.acceptRequestHistory
        ),
        baseInfo: {
          ...patient.baseInfo,
          name: patient?.baseInfo?.name
            ? patient.baseInfo.name
            : `傷病者${unknownCounter++}`,
          furigana: patient?.baseInfo?.furigana ?? '',
          birthday: patient?.baseInfo?.birthday ?? '',
          age: patient?.baseInfo?.age ?? '',
          patientAddress: patient?.baseInfo?.patientAddress ?? '',
          contactPatient: patient?.baseInfo?.contactPatient ?? '',
          accompanyingPerson: patient?.baseInfo?.accompanyingPerson ?? '',
          contactPerson: patient?.baseInfo?.contactPerson ?? '',
        },
        vitals:
          patient.vitals?.map(vital => {
            return {
              ...vital,
              data: {
                ...vital.data,
                observations: vital.data?.observations ?? [],
              },
            };
          }) ?? [],
        treatments:
          patient.treatments?.map(treatment => {
            return {
              ...treatment,
              data: {
                ...treatment.data,
                generalTreatments: treatment.data?.generalTreatments ?? [],
                executeTreatment: treatment.data?.executeTreatment ?? [],
                cpaData: {
                  ...treatment.data?.cpaData,
                  cpaNote: treatment.data?.cpaData?.cpaNote ?? '',
                },
                instructingDoctorName:
                  treatment.data?.instructingDoctorName ?? '',
              },
            };
          }) ?? [],
        trafficAccident: {
          ...patient.trafficAccident,
          accidentType: patient.trafficAccident?.accidentType ?? [],
        },
        findingsEmsContact: {
          ...patient.findingsEmsContact,
          just7: patient.findingsEmsContact?.just7 ?? [],
          reveals: {
            ...patient.findingsEmsContact?.reveals,
            paralysisParts:
              patient.findingsEmsContact?.reveals?.paralysisParts ?? [],
            paralysisSymptoms:
              patient.findingsEmsContact?.reveals?.paralysisSymptoms ?? [],
            spasmParts: patient.findingsEmsContact?.reveals?.spasmParts ?? [],
            bleedingType:
              patient.findingsEmsContact?.reveals?.bleedingType ?? [],
          },
        },
        activityRecordPdf: patient.activityRecordPdf.map(pdf => {
          const uploadAtString = formatDateTime(
            pdf.uploadedAt,
            'yyyyMMdd_HHmmss'
          );
          return {
            ...pdf,
            fileName: `${uploadAtString}_${emsTeamInfo.emsTeamName}.pdf`,
          };
        }),
        nonTransportPdf: patient.nonTransportPdf.map(pdf => {
          const uploadAtString = formatDateTime(
            pdf.uploadedAt,
            'yyyyMMdd_HHmmss'
          );
          return {
            ...pdf,
            fileName: `${uploadAtString}_${emsTeamInfo.emsTeamName}.pdf`,
          };
        }),
        emsReportPdf: patient.emsReportPdf.map(pdf => {
          const uploadAtString = formatDateTime(
            pdf.uploadedAt,
            'yyyyMMdd_HHmmss'
          );
          return {
            ...pdf,
            fileName: `${uploadAtString}_${emsTeamInfo.emsTeamName}.pdf`,
          };
        }),
        acceptRequestHistory: patient.acceptRequestHistory ?? [],
        imageIds: patient.imageIds ?? [],
        requestSummary: {
          ...patient.requestSummary,
          onsetAtText: patient.requestSummary?.onsetAtText ?? '',
          chiefComplaint: patient.requestSummary?.chiefComplaint ?? '',
          telegramSummary: patient.requestSummary?.telegramSummary ?? '',
          requestSummary: patient.requestSummary?.requestSummary ?? '',
          clinicalCourse: patient.requestSummary?.clinicalCourse ?? '',
          medicationStatus: patient.requestSummary?.medicationStatus ?? [],
          allergies: patient.requestSummary?.allergies ?? '',
          lastMealComment: patient.requestSummary?.lastMealComment ?? '',
        },
        incidentSituation: {
          ...patient.incidentSituation,
          thirdDegreeBurn:
            patient.findingsEmsContact?.reveals?.thirdDegreeBurn ?? '',
          secondDegreeBurn:
            patient.findingsEmsContact?.reveals?.secondDegreeBurn ?? '',
        },
      };
    }),
    emsTeamInfo: {
      ...emsTeamInfo,
      emsTeamMembers: emsTeamInfo.emsTeamMembers.map(member => ({
        ...member,
        name: member?.name ?? '',
      })),
    },
  };
};

/** 搬送先が決まった医療機関名を取得する関数。 */
const getDeterminedHospitalName = (
  acceptRequestHistory?: AcceptRequestHistorySchema[]
) => {
  if (!acceptRequestHistory || acceptRequestHistory.length === 0) {
    return '';
  }

  const destinationItem = [...acceptRequestHistory]
    .reverse()
    .find(item => item.requestStatus === 'DESTINATION_DETERMINED');

  return destinationItem?.destinationTarget?.hospitalName ?? '';
};
